import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Award, Camera, CheckCircle, GraduationCap, Plus, RefreshCw, Save, Search, ShieldCheck, Upload, User, UserCheck, X } from 'lucide-react';
import { BACKEND_URL, db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { getDisplayFileUrl } from '../../utils/mediaUrls';

const DEFAULT_DEPARTMENT = 'Engineering Sciences & Humanities';

const blankFaculty = {
  name: '',
  username: '',
  password: '',
  email: '',
  designation: '',
  phone: '',
  department: DEFAULT_DEPARTMENT
};

const DUTIES = [
  {
    role: 'classCoordinator',
    legacyRole: 'class_coordinator',
    label: 'Class Coordinator',
    shortLabel: 'Class Co-ordinator',
    icon: GraduationCap,
    field: 'division',
    fieldLabel: 'Division',
    placeholder: 'Select division',
    source: 'divisions'
  },
  {
    role: 'mentoringCoordinator',
    legacyRole: 'mentoring_coordinator',
    label: 'Mentoring Coordinator',
    shortLabel: 'Mentoring Co-ordinator',
    icon: ShieldCheck,
    field: 'scope',
    fieldLabel: 'Scope',
    placeholder: 'Department'
  },
  {
    role: 'mentor',
    legacyRole: 'mentor',
    label: 'Mentor',
    shortLabel: 'Mentor',
    icon: UserCheck,
    field: 'batch',
    fieldLabel: 'Batch',
    placeholder: 'Select batch',
    source: 'batches'
  }
];

const legacyRoleMap = DUTIES.reduce((acc, duty) => {
  acc[duty.legacyRole] = duty.role;
  return acc;
}, {});

const normalizeAssignment = (assignment = {}) => ({
  role: assignment.role,
  label: DUTIES.find((duty) => duty.role === assignment.role)?.label || assignment.label || assignment.role,
  division: assignment.division || '',
  batch: assignment.batch || '',
  scope: assignment.scope || ''
});

const assignmentFromLegacyUser = (user = {}) => {
  const role = legacyRoleMap[user.role] || user.role;
  if (!DUTIES.some((duty) => duty.role === role)) return null;

  return normalizeAssignment({
    role,
    division: user.assignedDivision || '',
    batch: user.assignedBatch || '',
    scope: role === 'mentoringCoordinator' ? 'department' : ''
  });
};

const getFacultyKey = (faculty) => faculty.email || faculty.id || faculty.username || faculty.loginId;

const profileFromFaculty = (faculty = {}) => ({
  name: faculty.name || '',
  username: faculty.username || faculty.loginId || '',
  email: faculty.email || '',
  designation: faculty.designation || 'Faculty',
  phone: faculty.phone || '',
  department: faculty.department || DEFAULT_DEPARTMENT,
  password: '',
  photoUrl: faculty.photoUrl || '',
  photoPath: faculty.photoPath || ''
});

const getSessionToken = () => {
  const sessionKeys = ['userSession', 'adminSession', 'studentSession'];

  for (const key of sessionKeys) {
    const rawSession = window.sessionStorage.getItem(key);
    if (!rawSession) continue;

    try {
      const token = JSON.parse(rawSession)?.sessionToken || '';
      if (token) return token;
    } catch (error) {
      console.warn(`Could not parse ${key}.`, error);
    }
  }

  return '';
};

const sortNames = (items) => [...items].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const FacultyRolesWidget = () => {
  const [faculty, setFaculty] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draftRoles, setDraftRoles] = useState([]);
  const [draftProfile, setDraftProfile] = useState(profileFromFaculty());
  const [newFaculty, setNewFaculty] = useState(blankFaculty);
  const [divisions, setDivisions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState('');
  const selectedKeyRef = useRef('');

  useEffect(() => {
    loadFaculty();
  }, []);

  const isAddingFaculty = selectedKey === '__new__';
  const selectedFaculty = faculty.find((item) => getFacultyKey(item) === selectedKey) || null;

  const updateSelectedKey = (nextKey) => {
    selectedKeyRef.current = nextKey || '';
    setSelectedKey(nextKey || '');
  };

  const filteredFaculty = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return faculty;

    return faculty.filter((item) => (
      (item.name || '').toLowerCase().includes(term) ||
      (item.email || '').toLowerCase().includes(term) ||
      (item.username || '').toLowerCase().includes(term)
    ));
  }, [faculty, search]);

  const loadFaculty = async () => {
    setLoading(true);
    setMessage('');

    try {
      const [usersSnap, assignmentSnap, studentsSnap, divisionSnap, batchSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'facultyAssignments')),
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'divisionAssignments')),
        getDocs(collection(db, 'batchAssignments'))
      ]);

      const assignmentMap = {};
      assignmentSnap.docs.forEach((assignmentDoc) => {
        assignmentMap[assignmentDoc.id] = assignmentDoc.data();
      });

      const divisionSet = new Set();
      const batchSet = new Set();

      studentsSnap.docs.forEach((studentDoc) => {
        const student = studentDoc.data();
        if (student.division) divisionSet.add(student.division);
        if (student.batch) batchSet.add(student.batch);
      });

      divisionSnap.docs.forEach((divisionDoc) => {
        const division = divisionDoc.data();
        if (division.division) divisionSet.add(division.division);
        if (division.name) divisionSet.add(division.name);
        if (divisionDoc.id) divisionSet.add(divisionDoc.id);
      });

      batchSnap.docs.forEach((batchDoc) => {
        const batch = batchDoc.data();
        if (batch.batch) batchSet.add(batch.batch);
        if (batch.name) batchSet.add(batch.name);
        if (batchDoc.id) batchSet.add(batchDoc.id);
      });

      const rows = usersSnap.docs
        .map((userDoc) => {
          const data = { id: userDoc.id, ...userDoc.data() };
          const key = getFacultyKey(data);
          const assignmentDoc = assignmentMap[key] || assignmentMap[data.email] || assignmentMap[data.username] || null;
          const roles = Array.isArray(assignmentDoc?.roles) && assignmentDoc.roles.length
            ? assignmentDoc.roles.map(normalizeAssignment)
            : [assignmentFromLegacyUser(data)].filter(Boolean);

          return {
            ...data,
            assignmentKey: key,
            assignments: roles
          };
        })
        .filter((item) => !['hod', 'administrator', 'admin'].includes(item.role))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

      setFaculty(rows);
      setDivisions(sortNames([...divisionSet]));
      setBatches(sortNames([...batchSet]));

      const currentSelectedKey = selectedKeyRef.current;
      if (currentSelectedKey === '__new__') return;

      const nextSelected = rows.some((item) => getFacultyKey(item) === currentSelectedKey)
        ? currentSelectedKey
        : getFacultyKey(rows[0] || {});

      updateSelectedKey(nextSelected || '');
      const selected = rows.find((item) => getFacultyKey(item) === nextSelected) || rows[0] || null;
      setDraftRoles(selected?.assignments || []);
      setDraftProfile(profileFromFaculty(selected || {}));
    } catch (error) {
      console.error('Error loading faculty management data:', error);
      setMessage('Could not load faculty management data.');
    } finally {
      setLoading(false);
    }
  };

  const selectFaculty = (item) => {
    updateSelectedKey(getFacultyKey(item));
    setDraftRoles(item.assignments || []);
    setDraftProfile(profileFromFaculty(item));
    setMessage('');
  };

  const startAddFaculty = () => {
    updateSelectedKey('__new__');
    setNewFaculty(blankFaculty);
    setDraftProfile(profileFromFaculty());
    setDraftRoles([]);
    setMessage('');
  };

  const cancelAddFaculty = () => {
    const first = faculty[0] || null;
    updateSelectedKey(first ? getFacultyKey(first) : '');
    setDraftRoles(first?.assignments || []);
    setDraftProfile(profileFromFaculty(first || {}));
    setNewFaculty(blankFaculty);
    setMessage('');
  };

  const hasRole = (role) => draftRoles.some((assignment) => assignment.role === role);

  const updateRole = (role, updates) => {
    setDraftRoles((current) => current.map((assignment) => (
      assignment.role === role ? { ...assignment, ...updates } : assignment
    )));
  };

  const updateDraftProfile = (field, value) => {
    setDraftProfile((current) => ({ ...current, [field]: value }));
    setMessage('');
  };

  const getDefaultRoleValue = (duty) => {
    if (duty.source === 'divisions') return divisions[0] || '';
    if (duty.source === 'batches') return batches[0] || '';
    return 'department';
  };

  const toggleRole = (duty) => {
    setMessage('');
    setDraftRoles((current) => {
      if (current.some((assignment) => assignment.role === duty.role)) {
        return current.filter((assignment) => assignment.role !== duty.role);
      }

      return [
        ...current,
        normalizeAssignment({
          role: duty.role,
          division: duty.field === 'division' ? getDefaultRoleValue(duty) : '',
          batch: duty.field === 'batch' ? getDefaultRoleValue(duty) : '',
          scope: duty.field === 'scope' ? 'department' : ''
        })
      ];
    });
  };

  const saveExistingAssignments = async () => {
    if (!selectedFaculty) return;

    const token = getSessionToken();
    if (!token) {
      throw new Error('HOD session expired. Please log out and sign in again with HOD Login.');
    }

    const roles = draftRoles.map(normalizeAssignment);
    const facultyKey = getFacultyKey(selectedFaculty);
    const loginId = draftProfile.username || selectedFaculty.username || selectedFaculty.loginId || facultyKey;
    const email = draftProfile.email || selectedFaculty.email || `${String(loginId).toLowerCase()}@ybit.local`;
    const payload = {
      ...draftProfile,
      name: draftProfile.name.trim(),
      username: String(loginId).trim(),
      loginId: String(loginId).trim(),
      email: String(email).trim(),
      designation: draftProfile.designation.trim() || 'Faculty',
      phone: draftProfile.phone.trim(),
      department: draftProfile.department.trim() || DEFAULT_DEPARTMENT,
      roles
    };

    if (!payload.name || !payload.username) {
      throw new Error('Faculty name and username are required.');
    }

    if (!payload.password) {
      delete payload.password;
    }

    const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/auth/faculty`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Could not save faculty member.');
    }

    const savedFaculty = result.faculty || payload;
    const savedRoles = Array.isArray(result.assignments) ? result.assignments.map(normalizeAssignment) : roles;
    const nextKey = getFacultyKey(savedFaculty) || facultyKey;

    setFaculty((current) => current.map((item) => (
      getFacultyKey(item) === facultyKey
        ? {
            ...item,
            ...savedFaculty,
            assignments: savedRoles
          }
        : item
    )));
    updateSelectedKey(nextKey);
    setDraftRoles(savedRoles);
    setDraftProfile(profileFromFaculty(savedFaculty));
    setMessage('Faculty updated.');
  };

  const buildNewFacultyPayload = () => {
    const payload = {
      ...newFaculty,
      name: newFaculty.name.trim(),
      username: newFaculty.username.trim(),
      password: newFaculty.password.trim(),
      email: newFaculty.email.trim(),
      designation: newFaculty.designation.trim(),
      phone: newFaculty.phone.trim(),
      department: newFaculty.department.trim() || 'Engineering Sciences & Humanities',
      roles: draftRoles.map(normalizeAssignment)
    };

    if (!payload.name || !payload.username) {
      throw new Error('Faculty name and username are required.');
    }

    if (!payload.password) {
      throw new Error('Password is required for a new faculty login.');
    }

    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      throw new Error('Enter a valid faculty email address or leave it blank.');
    }

    return payload;
  };

  const createFaculty = async () => {
    const token = getSessionToken();
    if (!token) {
      throw new Error('HOD session expired. Please log out and sign in again with HOD Login.');
    }

    const payload = buildNewFacultyPayload();
    const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/auth/faculty`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Could not create faculty member.');
    }

    await loadFaculty();
    const key = getFacultyKey(result.faculty || {}) || payload.email || `${payload.username.toLowerCase()}@ybit.local`;
    updateSelectedKey(key);
    setDraftRoles(Array.isArray(result.assignments) ? result.assignments.map(normalizeAssignment) : payload.roles);
    setDraftProfile(profileFromFaculty(result.faculty || payload));
    setNewFaculty(blankFaculty);
    setMessage('Faculty member added.');
  };

  const uploadExistingFacultyPhoto = async (file) => {
    if (!file || !selectedFaculty) return;

    setUploadingPhoto(true);
    setMessage('');

    try {
      const identity = draftProfile.username || selectedFaculty.username || selectedFaculty.loginId || getFacultyKey(selectedFaculty);
      const form = new FormData();
      form.append('file', file);
      form.append('facultyId', identity || 'faculty');
      form.append('username', draftProfile.username || selectedFaculty.username || selectedFaculty.loginId || '');
      form.append('email', draftProfile.email || selectedFaculty.email || '');
      form.append('uploadedBy', draftProfile.name || selectedFaculty.name || identity || 'HOD');
      form.append('oldPhotoPath', draftProfile.photoPath || selectedFaculty.photoPath || '');
      form.append('oldPhotoUrl', draftProfile.photoUrl || selectedFaculty.photoUrl || '');

      const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/upload/faculty-photo`, {
        method: 'POST',
        body: form
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Faculty photo upload failed.');
      }

      setDraftProfile((current) => ({
        ...current,
        photoUrl: result.photoUrl || result.fileUrl || '',
        photoPath: result.photoPath || result.filePath || ''
      }));
      setMessage('Photo uploaded. Click Save Faculty to keep it with this profile.');
    } catch (error) {
      console.error('Faculty photo upload failed:', error);
      setMessage(error.message || 'Faculty photo upload failed.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveFaculty = async () => {
    setSaving(true);
    setMessage('');

    try {
      if (isAddingFaculty) {
        await createFaculty();
      } else {
        await saveExistingAssignments();
      }
    } catch (error) {
      console.error('Error saving faculty:', error);
      setMessage(error.message || 'Could not save faculty.');
    } finally {
      setSaving(false);
    }
  };

  const renderRoleControl = (duty, assignment) => {
    if (duty.source === 'divisions' || duty.source === 'batches') {
      const options = duty.source === 'divisions' ? divisions : batches;
      const value = assignment?.[duty.field] || '';
      const allOptions = value && !options.includes(value) ? [value, ...options] : options;

      return (
        <select
          value={value}
          onChange={(event) => updateRole(duty.role, { [duty.field]: event.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="">{duty.placeholder}</option>
          {allOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return null;
  };

  const renderExistingProfileForm = () => {
    const photoUrl = getDisplayFileUrl(draftProfile.photoPath || draftProfile.photoUrl || '');

    return (
      <div className="grid gap-5 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-[170px_1fr]">
        <div className="flex flex-col items-center">
          <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-white">
            {photoUrl ? (
              <img src={photoUrl} alt="Faculty" className="h-full w-full object-cover" />
            ) : (
              <User className="h-12 w-12 text-gray-400" />
            )}
          </div>
          <label className="mt-3 inline-flex cursor-pointer items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
            {uploadingPhoto ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload Photo
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(event) => uploadExistingFacultyPhoto(event.target.files?.[0])}
              disabled={uploadingPhoto}
            />
          </label>
          <p className="mt-2 text-center text-xs text-gray-500">Saved in Google Drive</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Name</span>
            <input
              value={draftProfile.name}
              onChange={(event) => updateDraftProfile('name', event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Username</span>
            <input
              value={draftProfile.username}
              readOnly
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Email</span>
            <input
              value={draftProfile.email}
              readOnly
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Designation</span>
            <input
              value={draftProfile.designation}
              onChange={(event) => updateDraftProfile('designation', event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Phone</span>
            <input
              value={draftProfile.phone}
              onChange={(event) => updateDraftProfile('phone', event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">New Password</span>
            <input
              type="password"
              value={draftProfile.password}
              onChange={(event) => updateDraftProfile('password', event.target.value)}
              placeholder="Leave blank to keep current"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Department</span>
            <input
              value={draftProfile.department}
              onChange={(event) => updateDraftProfile('department', event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
        </div>
      </div>
    );
  };

  const renderFacultyForm = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Name</span>
        <input
          value={newFaculty.name}
          onChange={(event) => setNewFaculty((current) => ({ ...current, name: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Username</span>
        <input
          value={newFaculty.username}
          onChange={(event) => setNewFaculty((current) => ({ ...current, username: event.target.value.trim() }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Password</span>
        <input
          type="password"
          value={newFaculty.password}
          onChange={(event) => setNewFaculty((current) => ({ ...current, password: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Email</span>
        <input
          type="email"
          value={newFaculty.email}
          onChange={(event) => setNewFaculty((current) => ({ ...current, email: event.target.value.trim() }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Designation</span>
        <input
          value={newFaculty.designation}
          onChange={(event) => setNewFaculty((current) => ({ ...current, designation: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Phone</span>
        <input
          value={newFaculty.phone}
          onChange={(event) => setNewFaculty((current) => ({ ...current, phone: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
      <label className="block md:col-span-2">
        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Department</span>
        <input
          value={newFaculty.department}
          onChange={(event) => setNewFaculty((current) => ({ ...current, department: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </label>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <RefreshCw className="mr-3 h-5 w-5 animate-spin" />
        Loading faculty
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(260px,360px)_1fr]">
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <button
            type="button"
            onClick={startAddFaculty}
            className="inline-flex w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Faculty
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search faculty"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="max-h-[560px] overflow-y-auto">
          {isAddingFaculty && (
            <div className="border-b border-gray-100 bg-indigo-50 px-4 py-3">
              <div className="font-semibold text-gray-900">New Faculty Member</div>
              <div className="mt-1 text-xs text-gray-500">Unsaved</div>
            </div>
          )}

          {filteredFaculty.map((item) => {
            const key = getFacultyKey(item);
            const selected = key === selectedKey;

            return (
              <button
                key={key}
                type="button"
                onClick={() => selectFaculty(item)}
                className={`block w-full border-b border-gray-100 px-4 py-3 text-left transition-colors ${
                  selected ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="font-semibold text-gray-900">{item.name || key}</div>
                <div className="mt-1 truncate text-xs text-gray-500">{item.email || item.username || key}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(item.assignments || []).map((assignment) => (
                    <span
                      key={assignment.role}
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                    >
                      {DUTIES.find((duty) => duty.role === assignment.role)?.shortLabel || assignment.role}
                    </span>
                  ))}
                  {!item.assignments?.length && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                      No duty
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {filteredFaculty.length === 0 && !isAddingFaculty && (
            <div className="p-6 text-center text-sm text-gray-500">No faculty found.</div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        {selectedFaculty || isAddingFaculty ? (
          <>
            <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-5 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {isAddingFaculty ? 'New Faculty Member' : selectedFaculty.name || selectedFaculty.email}
                </h3>
                {!isAddingFaculty && (
                  <p className="mt-1 text-sm text-gray-500">{selectedFaculty.email || selectedFaculty.username}</p>
                )}
              </div>
              <div className="flex gap-2">
                {isAddingFaculty && (
                  <button
                    type="button"
                    onClick={cancelAddFaculty}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={saveFaculty}
                  disabled={saving || uploadingPhoto}
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {isAddingFaculty ? 'Create Faculty' : 'Save Faculty'}
                </button>
              </div>
            </div>

            {isAddingFaculty && (
              <div className="mb-6">
                {renderFacultyForm()}
              </div>
            )}

            {!isAddingFaculty && (
              <div className="mb-6 space-y-3">
                {renderExistingProfileForm()}
                <div className="flex items-start rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  <Camera className="mr-2 mt-0.5 h-4 w-4" />
                  Faculty profile edits update their login record, dashboard profile, and Google Drive photo reference.
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              {DUTIES.map((duty) => {
                const Icon = duty.icon;
                const active = hasRole(duty.role);
                const assignment = draftRoles.find((item) => item.role === duty.role);

                return (
                  <div
                    key={duty.role}
                    className={`rounded-lg border p-4 ${
                      active ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleRole(duty)}
                      className="flex w-full items-center justify-between text-left"
                    >
                      <span className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-full ${
                          active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="font-semibold text-gray-900">{duty.shortLabel}</span>
                      </span>
                      {active && <CheckCircle className="h-5 w-5 text-indigo-600" />}
                    </button>

                    {active && duty.source && (
                      <label className="mt-4 block">
                        <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                          {duty.fieldLabel}
                        </span>
                        {renderRoleControl(duty, assignment)}
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            {message && (
              <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                {message}
              </div>
            )}
          </>
        ) : (
          <div className="flex min-h-[280px] flex-col items-center justify-center text-gray-500">
            <Award className="mb-3 h-8 w-8" />
            Select faculty
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyRolesWidget;
